"use client";

import React, { ReactNode } from 'react';
import { motion, Variants } from 'framer-motion';
import { cn } from '@/lib/utils';

// Типы пресетов анимации
type AnimationPreset = 'fade' | 'slide' | 'scale' | 'blur' | 'blur-slide';

// Пресеты анимации для элементов группы
const presetVariants: Record<AnimationPreset, Variants> = {
  fade: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5 },
    },
  },
  slide: {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', bounce: 0.3, duration: 1 },
    },
  },
  scale: {
    hidden: { opacity: 0, scale: 0.85 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { type: 'spring', bounce: 0.3, duration: 1 },
    },
  },
  blur: {
    hidden: { opacity: 0, filter: 'blur(12px)' },
    visible: {
      opacity: 1,
      filter: 'blur(0px)',
      transition: { duration: 0.6 },
    },
  },
  // Основной пресет проекта: размытие + сдвиг снизу
  'blur-slide': {
    hidden: { opacity: 0, filter: 'blur(12px)', y: 12 },
    visible: {
      opacity: 1,
      filter: 'blur(0px)',
      y: 0,
      transition: { type: 'spring', bounce: 0.3, duration: 1.5 },
    },
  },
};

// Варианты контейнера с каскадной задержкой для дочерних элементов
const containerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

interface AnimatedGroupProps {
  children: ReactNode;
  className?: string;
  // Кастомные варианты анимации (переопределяют пресет)
  variants?: Variants;
  // Пресет анимации
  preset?: AnimationPreset;
}

export function AnimatedGroup({
  children,
  className,
  variants,
  preset = 'blur-slide',
}: AnimatedGroupProps) {
  // Используем кастомные варианты или выбранный пресет
  const itemVariants = variants ?? presetVariants[preset];

  return (
    <motion.div
      className={cn(className)}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {React.Children.map(children, (child, index) => (
        <motion.div key={index} variants={itemVariants}>
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
}
